import React, { useState } from 'react';
import {
  Box, Card, CardContent, Typography, Button, TextField, Alert,
  CircularProgress, Divider, Chip
} from '@mui/material';
import {
  Work as WorkIcon, Business as BusinessIcon
} from '@mui/icons-material';
import { useMutation, useQuery } from 'react-query';
import apiClient from '../../request/http-common';
import { useSelector } from 'react-redux';

const Employer = () => {
  const user = useSelector((state) => state.user.user);
  const [formData, setFormData] = useState({
    job_title: '',
    company_name: '',
    job_description: '',
    requirements: '',
    location: '',
    salary_range: '',
    job_type: 'full-time', // full-time, part-time, contract, remote
    experience_level: 'entry', // entry, mid, senior
    application_deadline: ''
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const jobPostingMutation = useMutation(
    async (jobData) => {
      const response = await apiClient.post('/api/jobs', jobData);
      return response.data;
    },
    {
      onSuccess: (data) => {
        setSuccess('Job posting submitted successfully! It is pending admin approval.');
        setFormData({
          job_title: '',
          company_name: '',
          job_description: '',
          requirements: '',
          location: '',
          salary_range: '',
          job_type: 'full-time',
          experience_level: 'entry',
          application_deadline: ''
        });
        setError('');
      },
      onError: (error) => {
        setError(error.response?.data?.message || 'Failed to submit job posting');
        setSuccess('');
      }
    }
  );

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!formData.job_title || !formData.company_name || !formData.job_description) {
      setError('Please fill in all required fields');
      return;
    }

    jobPostingMutation.mutate(formData);
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: '900px', mx: 'auto' }}>
      <Card sx={{
        background: 'linear-gradient(135deg, #161A1E 0%, #1E2328 100%)',
        border: '1px solid #2B3139',
        borderRadius: '12px'
      }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
            <BusinessIcon sx={{ fontSize: 32, color: '#F7931A' }} />
            <Typography variant="h4" sx={{ color: '#FFFFFF', fontWeight: 700 }}>
              Post a Job
            </Typography>
          </Box>

          <Typography variant="body2" sx={{ color: '#B7BDC6', mb: 3 }}>
            Create a job posting. All job postings require admin approval before being published.
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {success}
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <TextField
                fullWidth
                label="Job Title *"
                name="job_title"
                value={formData.job_title}
                onChange={handleChange}
                required
                sx={{
                  '& .MuiOutlinedInput-root': {
                    color: '#FFFFFF',
                    '& fieldset': { borderColor: '#2B3139' },
                    '&:hover fieldset': { borderColor: '#F7931A' },
                    '&.Mui-focused fieldset': { borderColor: '#F7931A' }
                  },
                  '& .MuiInputLabel-root': { color: '#B7BDC6' }
                }}
              />

              <TextField
                fullWidth
                label="Company Name *"
                name="company_name"
                value={formData.company_name}
                onChange={handleChange}
                required
                sx={{
                  '& .MuiOutlinedInput-root': {
                    color: '#FFFFFF',
                    '& fieldset': { borderColor: '#2B3139' },
                    '&:hover fieldset': { borderColor: '#F7931A' },
                    '&.Mui-focused fieldset': { borderColor: '#F7931A' }
                  },
                  '& .MuiInputLabel-root': { color: '#B7BDC6' }
                }}
              />

              <TextField
                fullWidth
                label="Job Description *"
                name="job_description"
                multiline
                rows={5}
                value={formData.job_description}
                onChange={handleChange}
                required
                sx={{
                  '& .MuiOutlinedInput-root': {
                    color: '#FFFFFF',
                    '& fieldset': { borderColor: '#2B3139' },
                    '&:hover fieldset': { borderColor: '#F7931A' },
                    '&.Mui-focused fieldset': { borderColor: '#F7931A' }
                  },
                  '& .MuiInputLabel-root': { color: '#B7BDC6' }
                }}
              />

              <TextField
                fullWidth
                label="Requirements"
                name="requirements"
                multiline
                rows={4}
                value={formData.requirements}
                onChange={handleChange}
                placeholder="List the requirements for this position..."
                sx={{
                  '& .MuiOutlinedInput-root': {
                    color: '#FFFFFF',
                    '& fieldset': { borderColor: '#2B3139' },
                    '&:hover fieldset': { borderColor: '#F7931A' },
                    '&.Mui-focused fieldset': { borderColor: '#F7931A' }
                  },
                  '& .MuiInputLabel-root': { color: '#B7BDC6' }
                }}
              />

              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <TextField
                  fullWidth
                  label="Location"
                  name="location"
                  value={formData.location}
                  onChange={handleChange}
                  sx={{
                    flex: 1,
                    minWidth: '200px',
                    '& .MuiOutlinedInput-root': {
                      color: '#FFFFFF',
                      '& fieldset': { borderColor: '#2B3139' },
                      '&:hover fieldset': { borderColor: '#F7931A' },
                      '&.Mui-focused fieldset': { borderColor: '#F7931A' }
                    },
                    '& .MuiInputLabel-root': { color: '#B7BDC6' }
                  }}
                />

                <TextField
                  fullWidth
                  label="Salary Range"
                  name="salary_range"
                  value={formData.salary_range}
                  onChange={handleChange}
                  placeholder="e.g., $50,000 - $70,000"
                  sx={{
                    flex: 1,
                    minWidth: '200px',
                    '& .MuiOutlinedInput-root': {
                      color: '#FFFFFF',
                      '& fieldset': { borderColor: '#2B3139' },
                      '&:hover fieldset': { borderColor: '#F7931A' },
                      '&.Mui-focused fieldset': { borderColor: '#F7931A' }
                  },
                  '& .MuiInputLabel-root': { color: '#B7BDC6' }
                  }}
                />
              </Box>

              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <TextField
                  select
                  fullWidth
                  label="Job Type"
                  name="job_type"
                  value={formData.job_type}
                  onChange={handleChange}
                  SelectProps={{
                    native: true,
                  }}
                  sx={{
                    flex: 1,
                    minWidth: '200px',
                    '& .MuiOutlinedInput-root': {
                      color: '#FFFFFF',
                      '& fieldset': { borderColor: '#2B3139' },
                      '&:hover fieldset': { borderColor: '#F7931A' },
                      '&.Mui-focused fieldset': { borderColor: '#F7931A' }
                    },
                    '& .MuiInputLabel-root': { color: '#B7BDC6' }
                  }}
                >
                  <option value="full-time">Full-time</option>
                  <option value="part-time">Part-time</option>
                  <option value="contract">Contract</option>
                  <option value="remote">Remote</option>
                </TextField>

                <TextField
                  select
                  fullWidth
                  label="Experience Level"
                  name="experience_level"
                  value={formData.experience_level}
                  onChange={handleChange}
                  SelectProps={{
                    native: true,
                  }}
                  sx={{
                    flex: 1,
                    minWidth: '200px',
                    '& .MuiOutlinedInput-root': {
                      color: '#FFFFFF',
                      '& fieldset': { borderColor: '#2B3139' },
                      '&:hover fieldset': { borderColor: '#F7931A' },
                      '&.Mui-focused fieldset': { borderColor: '#F7931A' }
                    },
                    '& .MuiInputLabel-root': { color: '#B7BDC6' }
                  }}
                >
                  <option value="entry">Entry Level</option>
                  <option value="mid">Mid Level</option>
                  <option value="senior">Senior Level</option>
                </TextField>
              </Box>

              <TextField
                fullWidth
                label="Application Deadline"
                name="application_deadline"
                type="date"
                value={formData.application_deadline}
                onChange={handleChange}
                InputLabelProps={{
                  shrink: true,
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    color: '#FFFFFF',
                    '& fieldset': { borderColor: '#2B3139' },
                    '&:hover fieldset': { borderColor: '#F7931A' },
                    '&.Mui-focused fieldset': { borderColor: '#F7931A' }
                  },
                  '& .MuiInputLabel-root': { color: '#B7BDC6' }
                }}
              />

              <Divider sx={{ borderColor: '#2B3139' }} />

              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                <Button
                  type="button"
                  variant="outlined"
                  onClick={() => {
                    setFormData({
                      job_title: '',
                      company_name: '',
                      job_description: '',
                      requirements: '',
                      location: '',
                      salary_range: '',
                      job_type: 'full-time',
                      experience_level: 'entry',
                      application_deadline: ''
                    });
                    setError('');
                    setSuccess('');
                  }}
                  sx={{
                    borderColor: '#2B3139',
                    color: '#B7BDC6',
                    '&:hover': { borderColor: '#6C7293' }
                  }}
                >
                  Clear
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={jobPostingMutation.isLoading}
                  startIcon={jobPostingMutation.isLoading ? <CircularProgress size={20} /> : <WorkIcon />}
                  sx={{
                    background: 'linear-gradient(135deg, #F7931A 0%, #FFD700 100%)',
                    color: '#000',
                    fontWeight: 600,
                    '&:hover': {
                      background: 'linear-gradient(135deg, #E8841A 0%, #E6C200 100%)'
                    }
                  }}
                >
                  {jobPostingMutation.isLoading ? 'Submitting...' : 'Submit Job Posting'}
                </Button>
              </Box>
            </Box>
          </form>
        </CardContent>
      </Card>
    </Box>
  );
};

export default Employer;

